import { useMemo } from 'react'
import { Crosshair, Radar, Radio, Satellite, TriangleAlert } from 'lucide-react'
import { EmptyHint } from '../ui/Panels'
import { StateBadge, StateDot, stateClass } from '../ui/State'
import type { UiState } from '../../types'
import type { TrafficTrack } from '../../lib/trackModel'

/* ============================================================================
 * ТАБЛИЦА ЦЕЛЕЙ ВОЗДУШНОЙ ОБСТАНОВКИ
 *
 * Цели делятся на:
 *   • кооперативные   — ADS-B, Remote ID (ЭРА-ГЛОНАСС, 1090 МГц);
 *   • некооперативные — РЛС, RF-пеленгование (идентификация не подтверждена);
 *   • нарушители      — вторжение в контролируемую зону, КРИТИЧЕСКИЙ ПРИОРИТЕТ.
 *
 * Строка получает CSS-класс состояния: selected / active / critical-error.
 * ========================================================================= */

export function TrackTable({
  tracks,
  selectedId,
  onSelect,
  lockedId,
}: {
  tracks: TrafficTrack[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** Зафиксированный «свой БВС» для инструмента Дальность и азимут */
  lockedId: string | null
}) {
  // Нарушители — первыми, затем некооперативные, затем кооперативные
  const sorted = useMemo(() => {
    const rank = (t: TrafficTrack): number =>
      t.kind === 'intruder' ? 0 : t.kind === 'noncooperative' ? 1 : 2
    return [...tracks].sort((a, b) => rank(a) - rank(b) || (b.altM ?? 0) - (a.altM ?? 0))
  }, [tracks])

  if (sorted.length === 0) {
    return (
      <EmptyHint>
        <span className="flex flex-col items-center gap-1.5">
          <Radar size={20} className="text-ink-muted" aria-hidden />
          Цели не обнаружены. Проверьте подключение приёмников ADS-B / РЛС.
        </span>
      </EmptyHint>
    )
  }

  return (
    <table className="ut-table">
      <thead>
        <tr>
          <th>Позывной</th>
          <th>Тип</th>
          <th title="Высота над уровнем моря (ASL)">Выс., м</th>
        <th title="Высота над поверхностью земли (AGL) = ASL − высота рельефа">AGL, м</th>
          <th>Скор., м/с</th>
          <th>Курс</th>
          <th>Ист.</th>
          <th title="Идентификатор источника данных">ИД ист.</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((t) => {
          const isSelected = t.id === selectedId
          const isLocked = t.id === lockedId
          const st: UiState =
            t.kind === 'intruder' ? 'critical-error' : isSelected ? 'selected' : 'active'
          return (
            <tr
              key={t.id}
              className={`cursor-pointer ${stateClass(st)}`}
              data-state={st}
              onClick={() => onSelect(t.id)}
              aria-selected={isSelected}
            >
              <td>
                <span className="flex items-center gap-1.5">
                  <StateDot state={st} />
                  <span className={t.kind === 'intruder' ? 'font-bold' : 'font-medium'}>
                    {t.callsign}
                  </span>
                  {isLocked && (
                    <Crosshair size={10} className="text-accent" aria-label="Свой БВС" />
                  )}
                </span>
              </td>
              <td>
                <span className="flex items-center gap-1 text-[10px] text-ink-muted">
                  {t.kind === 'cooperative' ? (
                    <Satellite size={10} aria-hidden />
                  ) : t.kind === 'noncooperative' ? (
                    <Radio size={10} aria-hidden />
                  ) : (
                    <TriangleAlert size={10} className="text-critical" aria-hidden />
                  )}
                  {t.source}
                </span>
              </td>
              <td className="ut-metric">{t.altM !== null ? t.altM.toFixed(0) : '—'}</td>
              <td
                className="ut-metric"
                title={
                  t.aglM === null
                    ? 'Высота над рельефом недоступна: нет DEM'
                    : `Высота над рельефом: ${t.aglM.toFixed(1)} м, точность рельефа ±${t.terrainUncertaintyM ?? '—'} м (растр ${t.terrainResolutionM ?? '—'} м/пикс). ` +
                      'Отрицательное значение означает, что источник сообщает высоту ниже поверхности земли.'
                }
              >
                {t.aglM === null ? (
                  '—'
                ) : (
                  <>
                    {t.aglM.toFixed(0)}
                    {/* Отрицательный AGL — не ошибка UI, а факт источника:
                        помечаем, но НЕ скрываем. */}
                    {t.aglM < 0 && (
                      <span className="text-[10px] text-warning" title="Источник сообщает высоту ниже поверхности земли">
                        ?
                      </span>
                    )}
                  </>
                )}
              </td>
              <td className="ut-metric">{t.speedMs !== null ? t.speedMs.toFixed(0) : '—'}</td>
              <td className="ut-metric">
                {t.headingDeg !== null ? `${t.headingDeg.toFixed(0).padStart(3, '0')}°` : '—'}
              </td>
              <td>
                <StateBadge state={st}>
                  {t.kind === 'intruder'
                    ? 'ВТОРЖЕНИЕ'
                    : t.kind === 'noncooperative'
                      ? 'НЕИДЕНТ.'
                      : 'ИДЕНТ.'}
                </StateBadge>
              </td>
              <td>
                {t.providerId ? (
                  <span
                    className="ut-metric truncate text-[10px] text-ink-muted"
                    title={`Источник: ${t.providerKind ?? 'не указан'} / ${t.providerId}`}
                  >
                    {t.providerId.length > 12 ? t.providerId.slice(0, 10) + '…' : t.providerId}
                  </span>
                ) : (
                  <span className="text-[10px] text-ink-muted/50" title="Источник не передан бэкендом">
                    —
                  </span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/** Сводка по целям: сколько кооперативных, некооперативных, нарушителей */
export function TrackSummary({ tracks }: { tracks: TrafficTrack[] }) {
  const counts = useMemo(
    () => ({
      cooperative: tracks.filter((t) => t.kind === 'cooperative').length,
      noncooperative: tracks.filter((t) => t.kind === 'noncooperative').length,
      intruder: tracks.filter((t) => t.kind === 'intruder').length,
    }),
    [tracks],
  )

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="flex items-center gap-1 text-[10px] text-ink-muted">
        <StateDot state="approved" />
        Кооперативных: <b className="ut-metric">{counts.cooperative}</b>
      </span>
      <span className="flex items-center gap-1 text-[10px] text-ink-muted">
        <StateDot state="warning" />
        Некооперативных: <b className="ut-metric">{counts.noncooperative}</b>
      </span>
      <span className="flex items-center gap-1 text-[10px] text-ink-muted">
        <StateDot state="critical-error" pulsing={counts.intruder > 0} />
        Нарушителей: <b className="ut-metric">{counts.intruder}</b>
      </span>
    </div>
  )
}
